import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Bell, Plus, CheckCircle2, Trash2, AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Announcements = () => {
  const { user, isOrganizer, canPostAnnouncements } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'normal'
  });

  // Can manage = organizer OR has committee permission
  const canManage = (isOrganizer && isOrganizer()) || canPostAnnouncements();

  useEffect(() => {
    if (isOrganizer && isOrganizer()) {
      fetchEvents();
    } else {
      // Attendee - use their event_id
      if (user?.event_id) {
        setSelectedEventId(user.event_id);
      }
    }
  }, [user, isOrganizer]);

  useEffect(() => {
    if (selectedEventId) {
      fetchAnnouncements();
    }
  }, [selectedEventId]);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/my`);
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEventId(response.data[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      toast.error('Failed to load events');
      setLoading(false);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/event/announcements?event_id=${selectedEventId}`);
      setAnnouncements(response.data);
    } catch (error) {
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/event/announcements?event_id=${selectedEventId}`, newAnnouncement);
      toast.success('Announcement created');
      setNewAnnouncement({ title: '', content: '', priority: 'normal' });
      setCreateDialogOpen(false);
      fetchAnnouncements();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleAcknowledge = async (announcementId) => {
    try {
      await axios.post(`${API_URL}/api/event/announcements/${announcementId}/acknowledge`);
      toast.success('Acknowledged');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to acknowledge');
    }
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/event/announcements/${announcementId}`);
      toast.success('Announcement deleted');
      fetchAnnouncements();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'important': return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      default: return <Bell className="h-5 w-5 text-green-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'important': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-maroon" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="announcements-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <Bell className="h-8 w-8" />
              Announcements
            </h1>
            <p className="text-gray-600 mt-1">
              Stay updated with the latest news
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Event Selector for Organizers */}
            {canManage && events.length > 1 && (
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select Event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id}>
                      {event.event_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {canManage && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-maroon hover:bg-maroon-hover text-white rounded-full" data-testid="create-announcement-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Announcement
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-maroon">Create Announcement</DialogTitle>
                    <DialogDescription>Share important news with all members</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Input
                        value={newAnnouncement.title}
                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                        placeholder="Announcement title"
                        required
                        data-testid="announcement-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select 
                        value={newAnnouncement.priority} 
                        onValueChange={(v) => setNewAnnouncement({ ...newAnnouncement, priority: v })}
                      >
                        <SelectTrigger data-testid="priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="important">Important</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Content</Label>
                      <Textarea
                        value={newAnnouncement.content}
                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                        placeholder="Write your announcement here..."
                        rows={5}
                        required
                        data-testid="announcement-content-input"
                      />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-maroon hover:bg-maroon-hover text-white" data-testid="submit-announcement-btn">
                        Post Announcement
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        {/* Announcements List */}
        {announcements.length === 0 ? (
          <Card className="border-0 shadow-sm bg-white">
            <CardContent className="p-12 text-center">
              <Bell className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No announcements yet</h3>
              <p className="text-gray-400 mt-1">Check back later for updates</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map((announcement) => {
              const isAcknowledged = announcement.acknowledged_by?.includes(user?.id);
              const acknowledgedCount = announcement.acknowledged_by?.length || 0;
              
              return (
                <Card 
                  key={announcement.id}
                  className={`border-0 shadow-sm transition-all duration-300 ${
                    isAcknowledged ? 'bg-gray-50' : 'bg-white hover:shadow-md'
                  }`}
                  data-testid={`announcement-card-${announcement.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 mt-1">
                        {getPriorityIcon(announcement.priority)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={`${getPriorityColor(announcement.priority)}`}>
                            {announcement.priority}
                          </Badge>
                          {isAcknowledged && (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              <CheckCircle2 className="h-3 w-3 mr-1" /> Acknowledged
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-gray-900">
                          {announcement.title}
                        </h3>
                        <p className="text-gray-600 mt-2 whitespace-pre-wrap">
                          {announcement.content}
                        </p>
                        <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-500">
                          <span>By {announcement.created_by_name}</span>
                          <span>•</span>
                          <span>{new Date(announcement.created_at).toLocaleDateString('en-PH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}</span>
                          <span>•</span>
                          <span>{acknowledgedCount} acknowledged</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {!isAcknowledged && (
                          <Button
                            onClick={() => handleAcknowledge(announcement.id)}
                            className="bg-maroon hover:bg-maroon-hover text-white"
                            data-testid={`acknowledge-btn-${announcement.id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Acknowledge
                          </Button>
                        )}
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(announcement.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-announcement-${announcement.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
  );
};

export default Announcements;
